import { useCallback, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

/**
 * La domanda «sei sicuro?», fatta dall'applicazione e non dal browser.
 *
 * `window.confirm` è una finestra di sistema: ha lo stile del browser e non
 * quello del gestionale, il testo del bottone non si può scrivere (resta «OK»),
 * e su alcuni browser l'utente può spegnerla del tutto — cioè un'azione che
 * cancella dati verrebbe eseguita senza che nessuno abbia confermato niente.
 *
 * Qui la stessa domanda passa da `AlertDialog`, che è già fra le primitive:
 * l'azione distruttiva è marcata come tale e il bottone porta il verbo di ciò
 * che sta per succedere, non «OK».
 *
 * Uso — il punto di chiamata resta una riga sola, come prima:
 *
 *   const { chiedi, dialogoConferma } = useConferma();
 *   …
 *   if (!(await chiedi({
 *     titolo: "Cancellare il preventivo?",
 *     descrizione: "Sparisce dall'elenco. Non si torna indietro.",
 *     verbo: "Cancella il preventivo",
 *   }))) return;
 *   …
 *   return (<>…{dialogoConferma}</>);
 */
export function useConferma() {
  const [domanda, setDomanda] = useState(null);
  const risolvi = useRef(null);

  const chiedi = useCallback(
    ({ titolo, descrizione, verbo = "Conferma", distruttiva = true }) =>
      new Promise((resolve) => {
        risolvi.current = resolve;
        setDomanda({ titolo, descrizione, verbo, distruttiva });
      }),
    [],
  );

  const chiudi = useCallback((risposta) => {
    setDomanda(null);
    const r = risolvi.current;
    risolvi.current = null;
    if (r) r(risposta);
  }, []);

  const dialogoConferma = (
    <AlertDialog
      open={domanda != null}
      onOpenChange={(aperto) => {
        // Chiusa con Esc, con un clic fuori o col bottone: è sempre un «no».
        if (!aperto) chiudi(false);
      }}
    >
      <AlertDialogContent data-testid="dialogo-conferma">
        <AlertDialogHeader>
          <AlertDialogTitle>{domanda?.titolo}</AlertDialogTitle>
          <AlertDialogDescription>{domanda?.descrizione}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="conferma-annulla">Annulla</AlertDialogCancel>
          <AlertDialogAction
            data-testid="conferma-procedi"
            className={cn(
              domanda?.distruttiva && buttonVariants({ variant: "destructive" }),
            )}
            onClick={() => chiudi(true)}
          >
            {domanda?.verbo}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { chiedi, dialogoConferma };
}
